import { addHours, subDays } from 'date-fns';
import { DateTime } from 'luxon';
import {
  EFA_BLOCKS_MARCH_CLOCK_CHANGE,
  EFA_BLOCKS_NORMAL_BST,
  EFA_BLOCKS_NORMAL_GMT,
  EFA_BLOCKS_OCTOBER_CLOCK_CHANGE,
  EFA_SUB_BLOCKS_MARCH_CLOCK_CHANGE,
  EFA_SUB_BLOCKS_NORMAL_BST,
  EFA_SUB_BLOCKS_NORMAL_GMT,
  EFA_SUB_BLOCKS_OCTOBER_CLOCK_CHANGE,
  LONG_PRODUCTS_MARCH_CLOCK_CHANGE,
  LONG_PRODUCTS_NORMAL_BST,
  LONG_PRODUCTS_NORMAL_GMT,
  LONG_PRODUCTS_OCTOBER_CLOCK_CHANGE,
} from 'src/consts/product-mappings';
import { UKEnergyProduct } from 'src/models/enums/products';
import { ProductTimeMap } from 'src/types/product-time-map';
import { ProductTimeSlot } from 'src/types/product-time-slot';
import { UK_TIMEZONE } from 'src/utils/uk-timezone';

export { UK_TIMEZONE };
export const UTC_TIMEZONE = 'UTC';
export const BST_OFFSET_MINS = 60;
export const SP_LENGTH_MINS = 30;
export const HOURS = 'hours';
const octoberMonthIndex = 9;
const marchMonthIndex = 2;

export function mapTimeSlotToProduct(
  startUTCTimestamp: number,
  endUTCTimestamp: number,
): UKEnergyProduct {
  const startDT = DateTime.fromSeconds(startUTCTimestamp, {
    zone: UK_TIMEZONE,
  });
  const endDT = DateTime.fromSeconds(endUTCTimestamp, { zone: UK_TIMEZONE });
  const durationHours = endDT.diff(startDT, HOURS).hours;
  const product = determineProduct(startDT, endDT, durationHours);
  if (product) return product;
  return UKEnergyProduct.NOT_APPLICABLE;
}

export function mapProductToTimeSlot(
  product: UKEnergyProduct,
  date: Date,
): ProductTimeSlot {
  const result = determineProductTimings(product, date);
  if (!result) {
    throw 'Unable to map product to time slot';
  }
  return result;
}

export function getEFAProductForTimeSlot(
  startUTCTimestamp: number,
): UKEnergyProduct {
  const startDT = DateTime.fromMillis(startUTCTimestamp, {
    zone: UTC_TIMEZONE,
  });
  const durationHours = 4;
  const startDate = calculateEFATradingDate(startDT, durationHours);
  const productList = createEFABlockProductList(startDate);

  const product = productList.find(
    (item) =>
      startUTCTimestamp >= +new Date(item.utcTimeStart) &&
      startUTCTimestamp < +new Date(item.utcTimeEnd),
  );

  if (product) return product.product as UKEnergyProduct;
  return UKEnergyProduct.NOT_APPLICABLE;
}

export function getSPProductForTimeSlot(
  startUTCTimestamp: number,
): UKEnergyProduct {
  const productList = createSPProductList(new Date(startUTCTimestamp));
  const product = productList.find(
    (item) =>
      startUTCTimestamp >= +new Date(item.ukTimeStart) &&
      startUTCTimestamp < +new Date(item.ukTimeEnd),
  );

  if (product) return product.product as UKEnergyProduct;
  return UKEnergyProduct.NOT_APPLICABLE;
}

export function isDateInBST(startDate: Date): boolean {
  const dateTime = DateTime.fromJSDate(startDate, { zone: UK_TIMEZONE });
  const start = dateTime.set({ hour: 0 });
  const end = dateTime.set({ hour: 23 });
  return start.offset === BST_OFFSET_MINS && end.offset === BST_OFFSET_MINS;
}

export function isDateInMarchClockChange(startDate: Date): boolean {
  const marchLastSunday = getLastSundayOfMonth(
    startDate.getFullYear(),
    marchMonthIndex,
  );
  const start = addHours(marchLastSunday, -1);
  const end = addHours(marchLastSunday, 23);
  return startDate >= start && startDate < end;
}

export function isDateInOctoberClockChange(startDate: Date): boolean {
  const octoberLastSunday = getLastSundayOfMonth(
    startDate.getFullYear(),
    octoberMonthIndex,
  );
  const start = addHours(octoberLastSunday, -1);
  const end = addHours(octoberLastSunday, 25);
  return startDate >= start && startDate < end;
}

export function getLastSundayOfMonth(year: number, month: number): Date {
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const dayOfWeek = lastDayOfMonth.getDay();
  const offset = dayOfWeek;
  return subDays(lastDayOfMonth, offset);
}

export function calculateEFATradingDate(
  startDT: DateTime,
  durationHours: number,
): Date {
  if (startDT.hour >= 23 && durationHours > 1) {
    return startDT.plus({ days: 1 }).toJSDate();
  }
  return startDT.toJSDate();
}

function generateProductMapKey(start: DateTime, end: DateTime): string {
  const duration = end.diff(start, HOURS).hours;
  return `${start.toUTC().day}-${start.toUTC().hour}-${end.toUTC().hour}-${duration}`;
}

function convertProductListToMap(
  productList: ProductTimeSlot[],
): Map<string, ProductTimeSlot> | undefined {
  return new Map(
    productList.map((item) => {
      const start = DateTime.fromISO(item.utcTimeStart);
      const end = DateTime.fromISO(item.utcTimeEnd);
      const key = generateProductMapKey(start, end);
      return [key, item] as [string, ProductTimeSlot];
    }),
  );
}

function determineProduct(
  startDT: DateTime,
  endDT: DateTime,
  durationHours: number,
): UKEnergyProduct | undefined {
  const startDate = calculateEFATradingDate(startDT, durationHours);
  let productList: ProductTimeSlot[] = [];
  if (durationHours > 5) {
    productList = createLongProductList(startDate);
  } else if (durationHours > 3 && durationHours <= 5) {
    productList = createEFABlockProductList(startDate);
  } else if (durationHours === 3) {
    productList = [
      ...createEFABlockProductList(startDate),
      ...createEFASubBlockProductList(startDate),
    ];
  } else if (durationHours >= 1 && durationHours < 3) {
    productList = [
      ...createEFASubBlockProductList(startDate),
      ...createHRProductList(startDate),
    ];
  } else if (durationHours === 0.5) {
    productList = createSPProductList(startDate);
  }
  const productMap = convertProductListToMap(productList);
  const key = generateProductMapKey(startDT, endDT);
  if (!productMap) return undefined;
  const foundProduct = productMap.get(key);
  // On clock change days, EFA1B is mapped to HR2 due to the duration being 1 hour in October
  if (
    foundProduct &&
    UKEnergyProduct[foundProduct.product] === UKEnergyProduct.EFA1B &&
    foundProduct?.durationHRs === 1
  )
    return UKEnergyProduct.HR2;
  return foundProduct?.product;
}

function determineProductTimings(
  specifiedProduct: UKEnergyProduct,
  date: Date,
): ProductTimeSlot | undefined {
  const productList = [
    ...createLongProductList(date),
    ...createEFABlockProductList(date),
    ...createEFASubBlockProductList(date),
    ...createEFASubBlockProductList(date),
    ...createHRProductList(date),
    ...createSPProductList(date),
  ];
  const productMap = new Map(
    productList.map((i) => [i.product, i] as [string, ProductTimeSlot]),
  );
  return productMap.get(specifiedProduct);
}

function createProductList(
  date: Date,
  normalProductTimeMapGMT: ProductTimeMap,
  normalProductTimeMapBST: ProductTimeMap,
  octoberClockChangeTimeMap: ProductTimeMap,
  marchClockChangeTimeMap: ProductTimeMap,
): any[] {
  const dateTimeUTC = DateTime.fromJSDate(date, { zone: UTC_TIMEZONE }).set({
    minute: 0,
  });
  const isBst = isDateInBST(date);
  const isMarchClockChange = isDateInMarchClockChange(date);
  const isOctoberClockChange = isDateInOctoberClockChange(date);

  let productTimeMap = isBst
    ? normalProductTimeMapBST
    : normalProductTimeMapGMT;
  if (isMarchClockChange) {
    productTimeMap = Object.assign(
      {},
      normalProductTimeMapBST,
      marchClockChangeTimeMap,
    );
  } else if (isOctoberClockChange) {
    productTimeMap = Object.assign(
      {},
      normalProductTimeMapGMT,
      octoberClockChangeTimeMap,
    );
  }
  return Object.keys(productTimeMap).map((product) => {
    const timings = productTimeMap[product];
    const utcTimeStart = dateTimeUTC
      .set({ hour: timings.startUTCHour })
      .plus({ days: timings.dateOffsetDays });
    const utcTimeEnd = dateTimeUTC.set({ hour: timings.endUTCHour });
    return {
      product: product,
      ukTimeStart: utcTimeStart.setZone(UK_TIMEZONE).toString(),
      ukTimeEnd: utcTimeEnd.setZone(UK_TIMEZONE).toString(),
      utcTimeStart: utcTimeStart.toString(),
      utcTimeEnd: utcTimeEnd.toString(),
      numberOfSPs:
        utcTimeEnd.diff(utcTimeStart, 'minutes').minutes / SP_LENGTH_MINS,
      durationHRs: utcTimeEnd.diff(utcTimeStart, 'hours').hours,
    };
  });
}

function createLongProductList(date: Date): ProductTimeSlot[] {
  return createProductList(
    date,
    LONG_PRODUCTS_NORMAL_GMT,
    LONG_PRODUCTS_NORMAL_BST,
    LONG_PRODUCTS_OCTOBER_CLOCK_CHANGE,
    LONG_PRODUCTS_MARCH_CLOCK_CHANGE,
  );
}

function createEFASubBlockProductList(date: Date): ProductTimeSlot[] {
  return createProductList(
    date,
    EFA_SUB_BLOCKS_NORMAL_GMT,
    EFA_SUB_BLOCKS_NORMAL_BST,
    EFA_SUB_BLOCKS_OCTOBER_CLOCK_CHANGE,
    EFA_SUB_BLOCKS_MARCH_CLOCK_CHANGE,
  );
}

export function createEFABlockProductList(date: Date): ProductTimeSlot[] {
  return createProductList(
    date,
    EFA_BLOCKS_NORMAL_GMT,
    EFA_BLOCKS_NORMAL_BST,
    EFA_BLOCKS_OCTOBER_CLOCK_CHANGE,
    EFA_BLOCKS_MARCH_CLOCK_CHANGE,
  );
}

export function createHRProductList(date: Date): ProductTimeSlot[] {
  const productTimeSlots: any[] = [];
  let currentTime = DateTime.fromJSDate(date, { zone: UK_TIMEZONE });

  if (currentTime.hour < 23)
    currentTime = currentTime.minus({ days: 1 }).set({
      hour: 23,
      minute: 0,
    });

  const nextDate = currentTime.plus({ days: 1 });

  let prev = currentTime.plus({ hours: -1 });
  for (
    let time = currentTime;
    time < nextDate;
    time = time.plus({ hours: 1 })
  ) {
    const ukTimeStart = time;
    const ukTimeEnd = time.plus({ hours: 1 });
    const extra = prev.hour === time.hour ? 'x' : '';
    const productHour = (ukTimeStart.hour + 2) % 24 || 24;
    const slot = {
      product: 'hr' + productHour + extra,
      ukTimeStart: ukTimeStart.toString(),
      ukTimeEnd: ukTimeEnd.toString(),
      utcTimeStart: ukTimeStart.setZone(UTC_TIMEZONE).toString(),
      utcTimeEnd: ukTimeEnd.setZone(UTC_TIMEZONE).toString(),
      numberOfSPs: 2,
      durationHRs: 1,
    };
    prev = time;
    productTimeSlots.push(slot);
  }
  return productTimeSlots;
}

export function createSPProductList(date: Date): ProductTimeSlot[] {
  const productTimeSlots: any[] = [];
  let startOfDay = DateTime.fromJSDate(date, { zone: UK_TIMEZONE }).set({
    hour: 0,
    minute: 0,
  });

  const nextDate = startOfDay.plus({ days: 1 });
  let i = 0;
  for (
    let time = startOfDay;
    time < nextDate;
    time = time.plus({ minutes: 30 })
  ) {
    i += 1;
    const ukTimeStart = time;
    const ukTimeEnd = time.plus({ minutes: 30 });
    const slot = {
      product: 'sp' + i,
      ukTimeStart: ukTimeStart.toString(),
      ukTimeEnd: ukTimeEnd.toString(),
      utcTimeStart: ukTimeStart.setZone(UTC_TIMEZONE).toString(),
      utcTimeEnd: ukTimeEnd.setZone(UTC_TIMEZONE).toString(),
      numberOfSPs: 1,
      durationHRs: 0.5,
    };
    productTimeSlots.push(slot);
  }
  return productTimeSlots;
}
